// Shared add/edit-student dialog — one implementation reused by the Students
// tab, a class roster, and the Student Detail view (brief §8: don't duplicate
// the edit UI). Students are identified by their 12-digit LRN, which is also
// what they bubble on the sheet (§7).
import { repos } from './store.js';
import { formDialog, toast } from './dom.js';
import type { ClassRecord, StudentRecord } from '../db/schema.js';

const LRN = '\\d{12}';

export async function studentFormDialog(opts: {
  existing?: StudentRecord;
  /** fixed class (add-from-roster or edit); omit to show a class picker. */
  classId?: string;
  /** classes to pick from when adding globally (no fixed class). */
  classes?: ClassRecord[];
  onDone?: () => void;
}): Promise<void> {
  const { existing } = opts;
  const editing = !!existing;
  const fixedClassId = existing?.classId ?? opts.classId;
  if (!editing && !fixedClassId && !(opts.classes && opts.classes.length)) return toast('Create a class first', 'err');

  const fields = [
    ...(!editing && !fixedClassId
      ? [{ name: 'classId', label: 'Class', type: 'select', value: opts.classes![0]?.id, options: opts.classes!.map((c) => ({ value: c.id, label: c.name })) }]
      : []),
    { name: 'name', label: 'Full name', required: true, value: existing?.name, maxlength: 80 },
    { name: 'lrn', label: 'LRN (12 digits)', value: existing?.lrn, required: true, pattern: LRN, inputmode: 'numeric', maxlength: 12, hint: 'The 12-digit LRN the student bubbles on the sheet.' },
    { name: 'sex', label: 'Sex', type: 'select', value: existing?.sex || '', options: [{ value: '', label: '—' }, { value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }] },
  ];
  const v = await formDialog(editing ? 'Edit student' : 'Add student', fields, editing ? 'Save' : 'Add');
  if (!v) return;
  try {
    if (editing) {
      await repos.updateStudent(existing!.id, { name: v.name, lrn: v.lrn, sex: v.sex as StudentRecord['sex'] });
      toast('Student updated');
    } else {
      await repos.addStudent({ classId: fixedClassId ?? v.classId, name: v.name, lrn: v.lrn, sex: v.sex as StudentRecord['sex'] });
      toast('Student added');
    }
    opts.onDone?.();
  } catch (e: any) {
    toast(e.message, 'err');
  }
}
